import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FlaskConical, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { isQuillEmpty } from "@/components/RichTextEditor";

// Gedeelde testverzend-logica: elk e-mail/WhatsApp-venster gebruikt dit blok (of het menu).
export function useTeamRecipients(enabled = true) {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    api.get("/team").then(({ data }) => setTeam((data || []).filter((m) => m.email))).catch(() => {});
  }, [enabled]);
  return team;
}

export async function sendCommTest({ channel, message, subject, eventId, toEmail, attachments }) {
  try {
    const { data } = await api.post("/comm/test-send", {
      channel, message, subject: subject || null,
      event_id: eventId || null, to_email: toEmail || null,
      attachments: channel === "email" && attachments?.length ? attachments : null,
    });
    if (data.ok) toast.success(data.message, { duration: 6000 });
    else toast.warning(data.message, { duration: 8000 });
    return data.ok;
  } catch (e) {
    toast.error(e.response?.data?.detail || "Testbericht versturen mislukt");
    return false;
  }
}

// Compact menu-variant: kies "Mijzelf" of een collega en verstuur direct (voor knoppen/iconen).
export function TestSendMenu({ trigger, onPick, testId = "comm-test-menu" }) {
  const [open, setOpen] = useState(false);
  const team = useTeamRecipients(open);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid={testId}>
        <DropdownMenuLabel className="text-xs text-slate-500">Test versturen naar…</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => onPick(null)} data-testid={`${testId}-self`}>Mijzelf</DropdownMenuItem>
        {team.map((m) => (
          <DropdownMenuItem key={m.email} onClick={() => onPick(m.email)}>{m.name || m.email}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Blok-variant: ontvanger-select + testknop, voor in verzenddialogen.
export default function TestSendBlock({ channel, message, subject, eventId, attachments, disabled, testId = "comm" }) {
  const team = useTeamRecipients();
  const [to, setTo] = useState("self");
  const [busy, setBusy] = useState(false);
  const empty = channel === "email" ? isQuillEmpty(message) : !(message || "").trim();
  const send = async () => {
    setBusy(true);
    await sendCommTest({ channel, message, subject, eventId, attachments, toEmail: to === "self" ? null : to });
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-2.5 space-y-2" data-testid={`${testId}-test-block`}>
      <p className="text-xs font-semibold text-slate-500 flex items-center gap-1">
        <FlaskConical className="h-3.5 w-3.5" />
        Eerst een test{channel === "whatsapp" ? "-WhatsApp" : "-e-mail"} naar jezelf of een collega?
      </p>
      <div className="flex items-center gap-2">
        <Select value={to} onValueChange={setTo}>
          <SelectTrigger className="h-8 flex-1 bg-white text-sm" data-testid={`${testId}-test-recipient`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="self">Mijzelf</SelectItem>
            {team.map((m) => <SelectItem key={m.email} value={m.email}>{m.name || m.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8 shrink-0 gap-1.5" onClick={send}
          disabled={disabled || busy || empty} data-testid={`${testId}-test-send-btn`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Test
        </Button>
      </div>
      <p className="text-[11px] text-slate-400">
        Tags worden ingevuld met de gegevens van de testontvanger{eventId ? " en dit event" : ""} — er gaat niets naar de echte ontvangers.
      </p>
    </div>
  );
}
